import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const ImpressumContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 4rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ContentCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: ${props => props.theme.shadows.card};
  margin-bottom: 2rem;
  
  h2 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    font-weight: 600;
  }
  
  p {
    line-height: 1.8;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 1.5rem;
    
    strong {
      color: ${props => props.theme.colors.text};
      font-weight: 600;
    }
    
    a {
      color: ${props => props.theme.colors.primary};
      text-decoration: none;
      transition: color 0.3s ease;
      
      &:hover {
        color: ${props => props.theme.colors.secondary};
        text-decoration: underline;
      }
    }
  }
`;

const Impressum = () => {
  return (
    <ImpressumContainer>
      <Helmet>
        <title>Impressum - Der Ponystall | Marcella Deeken</title>
        <meta name="description" content="Impressum und rechtliche Angaben von Der Ponystall - Marcella Deeken, tiergestützte Pädagogik und Förderung." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Impressum
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2>Dienstanbieter</h2>
            <p><strong>Der Ponystall – Marcella Deeken</strong><br />
            Tiergestützte Pädagogik & Förderung</p>

             <p><strong>Postanschrift:</strong><br />
             Weidenweg 2<br />
             26683 Saterland</p>

            <p><strong>Kontakt:</strong><br />
            Telefon: <a href="tel:01621353794">0162 1353794</a><br />
            E-Mail: <a href="mailto:derponystall@gmx.de">derponystall@gmx.de</a></p>

            <p><strong>Umsatzsteuer-ID:</strong><br />
            [falls vorhanden, § 27a UStG]</p>

            <p><strong>Inhaltlich Verantwortliche nach § 18 Abs. 2 MStV:</strong><br />
            Marcella Deeken, unter obiger Anschrift</p>
          </ContentCard>

          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2>Haftung für Inhalte</h2>
            <p>
              Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten
              nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als
              Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde
              Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige
              Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von
              Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.
            </p>
          </ContentCard>

          <ContentCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h2>Haftung für Links</h2>
            <p>
              Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen
              Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen.
              Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der
              Seiten verantwortlich.
            </p>
          </ContentCard>

           <ContentCard
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.6, delay: 0.8 }}
           >
             <h2>Hosting</h2>
             <p>
               <strong>IONOS SE</strong><br />
               Elgendorfer Str. 57<br />
               56410 Montabaur<br />
               Website: <a href="https://www.ionos.de" target="_blank" rel="noopener noreferrer">www.ionos.de</a>
             </p>
           </ContentCard>

           <ContentCard
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.6, delay: 1.0 }}
           >
             <h2>Technische Umsetzung & Design</h2>
             <p>
               <strong>DeekenDigital by Konstantin Deeken</strong><br />
               Weidenweg 2, 26683 Saterland<br />
               E-Mail: <a href="mailto:derponystall@gmx.de">derponystall@gmx.de</a><br />
               Website: <a href="https://deeken.digital" target="_blank" rel="noopener noreferrer">Deeken.Digital</a>
             </p>
           </ContentCard>

           <ContentCard
             initial={{ opacity: 0, y: 30 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.6, delay: 1.2 }}
           >
             <h2>Urheberrecht</h2>
             <p>
               Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen
               dem deutschen Urheberrecht. Beiträge Dritter sind als solche gekennzeichnet. Die
               Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der
               Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors
               bzw. Erstellers.
             </p>
           </ContentCard>
        </Container>
      </ContentSection>
    </ImpressumContainer>
  );
};

export default Impressum;


